import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Linkedin, MapPin, Clock, DollarSign, Briefcase, Anchor, AlertTriangle, RefreshCw, Crown, CheckCircle2, XCircle, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * /app/director/boarding — Director-only Foundry intake.
 *
 * Renders 28 active-interview candidates (Hawaii Strike Package) with
 * LinkedIn signal + skill chips + rate band + availability. The 12
 * high-mass B2B leads are mounted alongside in a sibling panel so the
 * Director can map candidates → strikes in one glance.
 *
 * If the data hasn't been seeded yet, the "Sync Hawaii Strike Package"
 * button lights up and primes the production collections in one click.
 */
export default function DirectorBoarding() {
    const [candidates, setCandidates] = useState([]);
    const [leads, setLeads] = useState([]);
    const [roleRequests, setRoleRequests] = useState([]);
    const [roleRequestCounts, setRoleRequestCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [roleFilter, setRoleFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [purging, setPurging] = useState(false);
    const [hardening, setHardening] = useState(false);
    const [purgingSynth, setPurgingSynth] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [c, l, rr] = await Promise.all([
                api.get("/director/boarding/candidates"),
                api.get("/director/boarding/leads"),
                api.get(`/director/boarding/role-requests?status=${roleFilter}`),
            ]);
            setCandidates(c.data?.candidates || []);
            setLeads(l.data?.leads || []);
            setRoleRequests(rr.data?.requests || []);
            setRoleRequestCounts(rr.data?.counts || { pending: 0, approved: 0, rejected: 0 });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not pull boarding manifest.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleFilter]);

    const decideRole = async (userId, action, name) => {
        try {
            const { data } = await api.post(`/director/boarding/role-requests/${userId}/${action}`);
            if (action === "approve") {
                toast.success(`${name || "Operator"} promoted · ${data.previous_role} → ${data.new_role}`);
            } else {
                toast.success(`${name || "Operator"} rejected · stays at ${data.stayed_at_role}`);
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || `${action} failed.`);
        }
    };

    const sync = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/director/seed/hawaii-strike-package");
            toast.success(
                `Delivery Package synced · ${data.totals.foundry_candidates} candidates · ${data.totals.b2b_leads} leads.`
            );
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sync failed.");
        } finally {
            setSyncing(false);
        }
    };

    const purgeTests = async () => {
        if (!window.confirm("Nuke ALL @test.com accounts and their derived metrics? This cannot be undone.")) return;
        setPurging(true);
        try {
            const { data } = await api.post("/director/purge/test-accounts");
            const d = data.deleted || {};
            toast.success(
                `Purge complete · ${d.users || 0} users · ${d.dashboard_metrics || 0} metrics · ${d.payment_transactions || 0} txns.`
            );
        } catch (e) {
            toast.error(e.response?.data?.detail || "Purge failed.");
        } finally {
            setPurging(false);
        }
    };

    const hardenReal = async () => {
        setHardening(true);
        try {
            const { data } = await api.post("/director/leads/harden");
            toast.success(`Hardened ${data.hardened?.length || 0} verified leads · synthetic=false.`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Harden failed.");
        } finally {
            setHardening(false);
        }
    };

    const purgeSynth = async () => {
        if (!window.confirm("Nuke ALL synthetic rows from b2b_leads + foundry_candidates? Hardened rows are safe.")) return;
        setPurgingSynth(true);
        try {
            const { data } = await api.post("/director/purge/synthetic");
            const d = data.deleted || {};
            toast.success(
                `Synthetic purged · ${d.b2b_leads || 0} leads + ${d.foundry_candidates || 0} candidates removed.`
            );
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Synthetic purge failed.");
        } finally {
            setPurgingSynth(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="director-boarding-page">
            <div className="fade-up flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">The Foundry · Boarding</div>
                    <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">Active Interviews</h1>
                    <p className="text-muted-foreground mt-2 max-w-2xl">
                        Live candidate manifest fed by the Hawaii Delivery Package. Map operators to leads, gate boarding,
                        and route accepted candidates into the Foundry queue.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="director-boarding-refresh"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[hsl(40_40%_30%/0.4)] text-xs uppercase tracking-wider hover:bg-[hsl(151_55%_13%)] transition-colors"
                    >
                        <RefreshCw size={13} /> Reload
                    </button>
                    <button
                        onClick={sync}
                        disabled={syncing}
                        data-testid="director-boarding-sync"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-xs uppercase tracking-wider hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-40"
                    >
                        <Anchor size={13} /> {syncing ? "Syncing…" : "Sync Delivery Package"}
                    </button>
                    <button
                        onClick={hardenReal}
                        disabled={hardening}
                        data-testid="director-boarding-harden"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[hsl(151_60%_30%)] text-[hsl(151_30%_95%)] font-semibold text-xs uppercase tracking-wider hover:bg-[hsl(151_65%_38%)] transition-colors disabled:opacity-40"
                    >
                        <Anchor size={13} /> {hardening ? "Hardening…" : "Harden Real Leads"}
                    </button>
                    <button
                        onClick={purgeSynth}
                        disabled={purgingSynth}
                        data-testid="director-boarding-purge-synthetic"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[hsl(195_60%_45%/0.5)] text-[hsl(195_75%_70%)] text-xs uppercase tracking-wider hover:bg-[hsl(195_60%_15%/0.5)] transition-colors disabled:opacity-40"
                    >
                        <AlertTriangle size={13} /> {purgingSynth ? "Purging…" : "Nuke Synthetic"}
                    </button>
                    <button
                        onClick={purgeTests}
                        disabled={purging}
                        data-testid="director-boarding-purge"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[hsl(350_60%_45%/0.5)] text-[hsl(350_75%_70%)] text-xs uppercase tracking-wider hover:bg-[hsl(350_60%_15%/0.5)] transition-colors disabled:opacity-40"
                    >
                        <AlertTriangle size={13} /> {purging ? "Purging…" : "Nuke @test.com"}
                    </button>
                </div>
            </div>

            {/* Stat strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 fade-up">
                <StatCard label="Pending Roles" value={roleRequestCounts.pending} accent="hsl(45 90% 60%)" testid="stat-pending-roles" />
                <StatCard label="Candidates" value={candidates.length} accent="hsl(40 61% 53%)" testid="stat-candidates" />
                <StatCard label="High-Mass Leads" value={leads.length} accent="hsl(350 75% 60%)" testid="stat-leads" />
                <StatCard label="Synthetic Rows" value={candidates.filter((c) => c.synthetic).length + leads.filter((l) => l.synthetic).length} accent="hsl(195 70% 55%)" testid="stat-synthetic" />
                <StatCard label="Anchor" value="HI" accent="hsl(151 60% 55%)" testid="stat-anchor" />
            </div>

            {/* Specialized Role Requests · Privateer / Admiral / Commander / Operative queue */}
            <div className="glass rounded-2xl p-6 fade-up" data-testid="role-request-queue">
                <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                    <div>
                        <div className="font-heading text-2xl">Specialized Role Requests</div>
                        <div className="text-xs text-muted-foreground">
                            Founding Privateers · TD Commanders · Sports Admirals · Field Operatives ·{" "}
                            <span className="text-[hsl(45_85%_70%)]">60/30/10 covenant enforced on every record</span>
                        </div>
                    </div>
                    <div className="inline-flex rounded-full border border-[hsl(40_40%_30%/0.3)] p-0.5 bg-[hsl(151_55%_10%)] text-[10px] uppercase tracking-wider">
                        {[
                            { id: "pending", count: roleRequestCounts.pending, color: "hsl(45 85% 65%)" },
                            { id: "approved", count: roleRequestCounts.approved, color: "hsl(151 60% 65%)" },
                            { id: "rejected", count: roleRequestCounts.rejected, color: "hsl(350 70% 70%)" },
                            { id: "all", count: roleRequestCounts.pending + roleRequestCounts.approved + roleRequestCounts.rejected },
                        ].map((f) => (
                            <button
                                key={f.id}
                                onClick={() => setRoleFilter(f.id)}
                                data-testid={`role-filter-${f.id}`}
                                className={`px-3 py-1.5 rounded-full transition-colors ${roleFilter === f.id ? "bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                            >
                                {f.id} {f.count != null && <span className="opacity-70 ml-0.5">({f.count})</span>}
                            </button>
                        ))}
                    </div>
                </div>
                {loading ? (
                    <Skeleton lines={3} />
                ) : roleRequests.length === 0 ? (
                    <Empty msg={`No role requests in "${roleFilter}" status.`} />
                ) : (
                    <div className="space-y-3" data-testid="role-request-list">
                        {roleRequests.map((r) => (
                            <RoleRequestRow key={r.id} req={r} onDecide={decideRole} />
                        ))}
                    </div>
                )}
            </div>

            {/* High-mass leads strip */}
            <div className="glass rounded-2xl p-6 fade-up">
                <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                    <div>
                        <div className="font-heading text-2xl">High-Mass B2B Leads</div>
                        <div className="text-xs text-muted-foreground">Hawaii Strike Package · red pulses on Sovereign Eye</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(350_75%_70%)]">{leads.length} ACTIVE</div>
                </div>
                {loading ? (
                    <Skeleton lines={3} />
                ) : leads.length === 0 ? (
                    <Empty msg="No leads yet. Click Sync Delivery Package to materialize the Hawaii dataset." />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {leads.map((l) => (
                            <LeadCard key={l.id} lead={l} />
                        ))}
                    </div>
                )}
            </div>

            {/* Candidate grid */}
            <div className="glass rounded-2xl p-6 fade-up">
                <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                    <div>
                        <div className="font-heading text-2xl">Active Interview Candidates</div>
                        <div className="text-xs text-muted-foreground">LinkedIn signal · skill chips · rate band · availability</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_70%)]">{candidates.length} CANDIDATES</div>
                </div>
                {loading ? (
                    <Skeleton lines={6} />
                ) : candidates.length === 0 ? (
                    <Empty msg="No candidates yet. Click Sync Delivery Package to materialize the 28-row interview manifest." />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="director-boarding-grid">
                        {candidates.map((c) => (
                            <CandidateCard key={c.id} candidate={c} />
                        ))}
                    </div>
                )}
            </div>

            {/* Synthetic disclosure */}
            {(candidates.some((c) => c.synthetic) || leads.some((l) => l.synthetic)) && (
                <div className="rounded-xl border border-[hsl(195_70%_55%/0.3)] bg-[hsl(195_50%_8%/0.5)] p-4 text-xs text-[hsl(195_50%_80%)] fade-up" data-testid="synthetic-warning">
                    <strong className="text-[hsl(195_85%_75%)]">⚠ Synthetic data live.</strong> Rows tagged{" "}
                    <code className="font-mono px-1.5 py-0.5 rounded bg-[hsl(195_55%_15%)]">synthetic: true</code> are
                    placeholder records seeded for the Hawaii Delivery Package. Hand-edit LinkedIn URLs and contact info
                    against your real CRM before treating them as live ops. Future endpoint: <code>POST /api/director/boarding/candidates/:id</code> to swap in real data.
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, accent, testid }) {
    return (
        <div className="glass rounded-xl p-4" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            <div className="font-heading text-3xl mt-1" style={{ color: accent }}>{value}</div>
        </div>
    );
}

function LeadCard({ lead }) {
    return (
        <a
            href={lead.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-lg border border-[hsl(350_60%_45%/0.3)] bg-[hsl(350_30%_10%/0.4)] p-3 hover:border-[hsl(350_75%_60%)] hover:bg-[hsl(350_30%_15%/0.5)] transition-colors"
            data-testid={`lead-card-${lead.short_name?.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="font-heading text-base text-[hsl(45_85%_72%)]">{lead.short_name || lead.company}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                        {(lead.sector || "—").replace(/_/g, " ")}
                    </div>
                </div>
                <Linkedin size={14} className="text-[hsl(195_70%_60%)] shrink-0 mt-1" />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">{lead.email}</div>
            <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wider">
                <MapPin size={10} className="text-[hsl(151_60%_55%)]" />
                <span>{lead.state}</span>
                {lead.priority === "high_mass" && (
                    <span className="ml-auto px-1.5 py-0.5 rounded bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold">HIGH-MASS</span>
                )}
            </div>
        </a>
    );
}

function CandidateCard({ candidate }) {
    return (
        <article
            className="rounded-xl border border-[hsl(40_40%_30%/0.3)] bg-[hsl(151_55%_10%/0.7)] p-4 hover:border-[hsl(40_61%_53%)] transition-colors"
            data-testid={`candidate-card-${candidate.full_name?.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="font-heading text-lg text-foreground truncate">{candidate.full_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{candidate.title}</div>
                </div>
                <a
                    href={candidate.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 p-1.5 rounded-md border border-[hsl(195_50%_30%/0.4)] text-[hsl(195_70%_65%)] hover:bg-[hsl(195_55%_15%)] transition-colors"
                    aria-label={`LinkedIn · ${candidate.full_name}`}
                    data-testid={`candidate-linkedin-${candidate.full_name?.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    <Linkedin size={14} />
                </a>
            </div>

            {/* Skill chips */}
            <div className="mt-3 flex flex-wrap gap-1.5">
                {(candidate.skills || []).map((s) => (
                    <span
                        key={s}
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(40_50%_25%/0.4)] text-[hsl(45_70%_75%)] border border-[hsl(40_50%_35%/0.3)]"
                    >
                        {s}
                    </span>
                ))}
            </div>

            {/* Meta strip */}
            <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                <Meta icon={MapPin}>{candidate.location}</Meta>
                <Meta icon={DollarSign}>{candidate.rate_band}</Meta>
                <Meta icon={Clock}>{candidate.availability}</Meta>
                <Meta icon={Briefcase}>
                    <span className="text-[hsl(151_60%_70%)]">{(candidate.stage || "").replace(/_/g, " ")}</span>
                </Meta>
            </div>
        </article>
    );
}

function Meta({ icon: Icon, children }) {
    return (
        <div className="flex items-center gap-1.5 min-w-0">
            <Icon size={11} className="text-[hsl(40_50%_55%)] shrink-0" />
            <span className="truncate">{children}</span>
        </div>
    );
}

function Skeleton({ lines = 3 }) {
    return (
        <div className="space-y-3 animate-pulse">
            {Array.from({ length: lines }).map((_, i) => (
                <div key={i} className="h-16 rounded-md bg-[hsl(151_55%_13%)]" />
            ))}
        </div>
    );
}

function Empty({ msg }) {
    return <div className="text-sm text-muted-foreground py-8 text-center">{msg}</div>;
}

const ROLE_LABELS = {
    founding_privateer: { label: "Founding Privateer", icon: Crown, accent: "hsl(45 85% 65%)" },
    td_commander:       { label: "TD Commander",       icon: Briefcase, accent: "hsl(195 70% 60%)" },
    sports_admiral:     { label: "Sports Admiral",     icon: Anchor, accent: "hsl(151 60% 55%)" },
    field_operative:    { label: "Field Operative",    icon: Users, accent: "hsl(280 60% 65%)" },
};

function RoleRequestRow({ req, onDecide }) {
    const meta = ROLE_LABELS[req.role_request] || { label: req.role_request, icon: Users, accent: "hsl(40 50% 55%)" };
    const Icon = meta.icon;
    const status = req.role_request_status || "pending";
    const statusStyle = {
        pending:  "bg-[hsl(45_50%_25%/0.5)] text-[hsl(45_85%_72%)]",
        approved: "bg-[hsl(151_55%_22%/0.5)] text-[hsl(151_60%_75%)]",
        rejected: "bg-[hsl(350_55%_25%/0.5)] text-[hsl(350_75%_75%)]",
    }[status];
    const covenantSum = (req.partner_share || 0) + (req.platform_share || 0) + (req.director_share || 0);
    const covenantOk = Math.abs(covenantSum - 1.0) < 0.001;

    return (
        <article
            className="rounded-xl border border-[hsl(40_40%_30%/0.3)] bg-[hsl(151_55%_10%/0.6)] p-4 hover:border-[hsl(40_50%_45%/0.6)] transition-colors"
            data-testid={`role-request-${req.email?.split("@")[0]}`}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: meta.accent + "22", color: meta.accent }}>
                        <Icon size={16} strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                        <div className="font-heading text-base text-foreground">{req.name || req.email}</div>
                        <div className="text-xs text-muted-foreground truncate">{req.email}{req.agency_name && ` · ${req.agency_name}`}</div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wider">
                            <span className="px-2 py-0.5 rounded-full" style={{ background: meta.accent + "22", color: meta.accent }}>
                                Requesting · {meta.label}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full ${statusStyle}`}>
                                {status}
                            </span>
                            {req.location_state && (
                                <span className="px-2 py-0.5 rounded-full bg-[hsl(40_40%_25%/0.3)] text-muted-foreground">
                                    <MapPin size={9} className="inline mr-0.5 -mt-px" />{req.location_state}
                                </span>
                            )}
                            <span
                                className={`px-2 py-0.5 rounded-full ${covenantOk ? "bg-[hsl(151_55%_22%/0.4)] text-[hsl(151_60%_70%)]" : "bg-[hsl(350_55%_25%/0.5)] text-[hsl(350_75%_75%)]"}`}
                                title="60/30/10 covenant on this user record"
                            >
                                {covenantOk ? "✓ 60/30/10" : `⚠ ${(req.partner_share*100).toFixed(0)}/${(req.platform_share*100).toFixed(0)}/${(req.director_share*100).toFixed(0)}`}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {req.linkedin_url && (
                        <a
                            href={req.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-md border border-[hsl(195_50%_30%/0.4)] text-[hsl(195_70%_65%)] hover:bg-[hsl(195_55%_15%)] transition-colors"
                            aria-label="LinkedIn"
                            data-testid={`role-request-linkedin-${req.email?.split("@")[0]}`}
                        >
                            <Linkedin size={14} />
                        </a>
                    )}
                    {status === "pending" && (
                        <>
                            <button
                                onClick={() => onDecide(req.id, "approve", req.name || req.email)}
                                data-testid={`role-request-approve-${req.email?.split("@")[0]}`}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[hsl(151_60%_30%)] text-[hsl(151_30%_95%)] text-xs uppercase tracking-wider font-semibold hover:bg-[hsl(151_65%_38%)] transition-colors"
                            >
                                <CheckCircle2 size={13} /> Approve
                            </button>
                            <button
                                onClick={() => onDecide(req.id, "reject", req.name || req.email)}
                                data-testid={`role-request-reject-${req.email?.split("@")[0]}`}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[hsl(350_60%_45%/0.5)] text-[hsl(350_75%_70%)] text-xs uppercase tracking-wider hover:bg-[hsl(350_60%_15%/0.4)] transition-colors"
                            >
                                <XCircle size={13} /> Reject
                            </button>
                        </>
                    )}
                </div>
            </div>
        </article>
    );
}
